<template>
	<SuiSkeleton
		v-if="showLoader"
		:aria-label="$t('spa__general.loading')"
		width="100%"
	/>

	<div v-else class="resource-planning__header">
		<ResourcePlanningChartHeader />

		<ResourcePlanningControls
			v-model:heat-map-value="heatMapValue"
			v-model:filter-value="bookingTypeFilter"
			:zoom-level="zoomLevel"
			:project-id="projectId"
			show-zoom-level-select
			show-zoom-to-fit
			@zoom-in="zoomIn()"
			@zoom-out="zoomOut()"
			@zoom-to="zoomTo($event)"
			@zoom-to-fit="zoomToFit()"
			@update:filter-value="updateResourceData"
		/>
	</div>

	<div
		:id="RESOURCE_PLANNING_CONTAINER_ID"
		ref="resourcePlanningTool"
		class="theme-gantt-chart"
	/>

	<ResourcePlanningChartInteraction
		v-if="resourcePlanningTool && !isReadOnly"
		:container="resourcePlanningTool"
		:config="config"
		@click="onChartClick"
	/>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue';
import {
	useFormatter,
	usePermissions,
	useSocket,
	useTranslation,
} from '@/composables';
import {
	addPlaceholderUnderProject,
	cleanUpVueInstances,
	openProjectMemberDropdown,
	fixTooltipPositioning,
	getChartResourceId,
	closePopOver,
	getResourceId,
	openBookingModal,
	isBookingVisible,
	getProjectPlannedDates,
	getResourceAndBookingsRow,
	getResourceType,
	openProjectsPopover,
	addTimeOffHighlightClickEventHandler,
	openConfirmationModal,
} from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { ResourcePlanningGanttConfig } from '../ResourcePlanningGantt/ResourcePlanningGanttConfig';
import {
	type ResourcePlanningGantt as Gantt,
	type ResourcePlanningItem,
	type ChartInteractionPayload,
	type ProjectMembers,
	type ProjectPlaceholderRoles,
	type BookingsWithoutResource,
} from '../../types';
import {
	DEFAULT_BOOKING_AMOUNT,
	RESOURCE_PROPERTY,
	ZOOM_SCALE_OPTIONS,
	CHART_ITEM_TYPE,
	CHART_ROW_TYPE,
	UTILIZATION_FETCH_INTERVAL,
	RESOURCE_PLANNING_CONTAINER_ID,
} from '../../constants';
import { SuiSkeleton } from '@developers/scoro-ui';
import { type NotWorthIt } from '@/types/utils';
import {
	BookingAmountType,
	type ResourcePlanningQuery,
	useResourcePlanningQuery,
	useResourceBookingDeleteMutation,
	ResourcePlanningUtilizationDocument,
	useResourcePlanningProjectAddMemberMutation,
	useResourcePlanningProjectRemoveMemberMutation,
	useResourceBookingsMoveMutation,
	type BookingSaveInput,
	type User,
	type Role,
	type RoleProject,
	TYPES,
	type ResourcePlanningUtilizationQuery,
	type Project,
} from '@/generated/graphql';
import { getUniqueId } from '@/utils/misc';
import { SocketMessageType } from '@/constants/socketMessages';
import { addEventListener } from '../ResourcePlanningGantt/ResourcePlanningGanttGlobalEventListeners';
import ResourcePlanningChartInteraction from './ResourcePlanningChartInteraction.vue';
import { type ResourcePlanningBookingModalProps } from '../ResourcePlanningBookingModal';
import { type Placement } from '@popperjs/core';
import { useMessage } from '@/features/Message';
import { addDays, isBefore, isAfter, addMonths } from 'date-fns';
import { resetTime } from '@/utils/formatting';
import { Constants } from '@/constants';
import { useBooleanSetting } from '@/composables/settings/useSettings';
import { MISSING_AVAILABILITY_LOADED } from '@/features/Availability';
import {
	ResourcePlanningControls,
	useResourcePlanningControls,
} from '../ResourcePlanningControls';
import { sortAlphabetically, sortAlphabeticallyBySubKey } from '@/utils/sort';
import { useResourcePlanningStore } from '../../store';
import { useResourcePlanningTimeOff } from '../../composables/ResourcePlanningTimeOff';
import { useApolloClient } from '@vue/apollo-composable';
import ResourcePlanningChartHeader from './ResourcePlanningChartHeader.vue';
import { type BaseSocketMessage } from '@/composables/socket';
import { PROJECT_TABS } from '@/features/Project';
import { useResourcePlanningUtils } from '@/features/ResourcePlanning/components/ResourcePlanningGantt/useResourcePlanningUtils';
import { getProjectIconName } from '@/features/Project/helpers';

export type ResourcePlanningChartProps = {
	projectId: Project['id'];
	retainerPeriodId?: number;
};

const props = defineProps<ResourcePlanningChartProps>();

const { t } = useTranslation();
const { addSuccessMessage } = useMessage();
const { userHas } = usePermissions();
const resourcePlanningStore = useResourcePlanningStore();
const { dateToMysqlString } = useFormatter();
const { client } = useApolloClient();
const [isRolesSettingEnabled] = useBooleanSetting(Constants.Setting.USE_ROLES);

const projectColor = ref('');
const isInitialLoad = ref(true); // Used for first time init
const isInitialRender = ref(true); // Used for first time render on each init/re-init
const projectMembers = ref<ProjectMembers>([]);
const projectPlaceholderRoles = ref<ProjectPlaceholderRoles>([]);
const bookingsWithoutResource = ref<BookingsWithoutResource>({});
const resourcePlanningTool = ref<HTMLDivElement | null>(null);

let gantt = window.Gantt.getGanttInstance() as Gantt;
const config = new ResourcePlanningGanttConfig(gantt);

watch(
	() => props.retainerPeriodId,
	(newRetainerPeriodId) => {
		config.setRetainerPeriodId(newRetainerPeriodId?.toString() || '');
		config.refreshDataWithoutFocus();
	}
);

const { loadTimeOffData } = useResourcePlanningTimeOff(config);

const { saveBookingMutation, handleErrorResponse } = useResourcePlanningUtils();

const projectMemberIds = computed(() =>
	projectMembers.value.map((user) => user.id)
);

const projectRoleIds = computed<Array<Role['id']>>(
	() =>
		projectPlaceholderRoles.value?.map(
			(roleProject) => roleProject.role.id
		) || []
);

const isRolesAsProjectMembersEnabled = computed(() =>
	userHas(Constants.Feature.ROLES_AS_PROJECT_MEMBERS)
);

const isRolesEnabled = computed(
	() => isRolesSettingEnabled.value && isRolesAsProjectMembersEnabled.value
);

const hasTentativeBookingsFeature = computed(() =>
	userHas(Constants.Feature.TENTATIVE_BOOKINGS)
);

const fetchedUtilizationDataInterval: {
	startDate?: Date;
	endDate?: Date;
} = {};

const { mutate: addProjectMembersMutation, onError: onAddProjectMembersError } =
	useResourcePlanningProjectAddMemberMutation({});

const {
	mutate: removeProjectMembersMutation,
	onError: onRemoveProjectMembersError,
} = useResourcePlanningProjectRemoveMemberMutation({});

const { mutate: deleteBookings, onError: onDeleteBookingsError } =
	useResourceBookingDeleteMutation({});

const {
	mutate: moveBookings,
	onDone: onMoveBookingsDone,
	onError: onMoveBookingsError,
} = useResourceBookingsMoveMutation({});

onAddProjectMembersError(handleErrorResponse);
onRemoveProjectMembersError(handleErrorResponse);
onDeleteBookingsError(handleErrorResponse);
onMoveBookingsError(handleErrorResponse);

onMoveBookingsDone(() => {
	addSuccessMessage({
		message: t('resource-booking__move-success'),
	});
});

const {
	onResult: onResultResourcePlanning,
	loading,
	result: resultResourcePlanning,
	refetch: refetchResourcePlanning,
} = useResourcePlanningQuery(
	{
		projectInput: {
			projectId: Number(props.projectId),
		},
		bookingsListInput: {
			filters: {
				projectIds: [props.projectId],
			},
		},
	},
	{
		fetchPolicy: 'no-cache',
		notifyOnNetworkStatusChange: true,
	}
);

const showLoader = computed(() => loading.value && isInitialLoad.value);

const isReadOnly = computed(
	() => !resultResourcePlanning.value?.Project?.permissions?.canModify
);

const {
	zoomLevel,
	zoomIn,
	zoomOut,
	zoomTo,
	zoomToFit,
	zoomToLevel,
	heatMapValue,
	getBookingStartAndEndDateByZoomLevel,
	bookingTypeFilter,
} = useResourcePlanningControls({
	config,
	projectId: props.projectId,
});

const onClearBookings = (
	id: User['id'] | RoleProject['id'],
	entityType: typeof TYPES.RoleProject | typeof TYPES.User
) => {
	deleteBookings({
		input: {
			projectId: String(props.projectId),
			userId: entityType === TYPES.User ? id : undefined,
			roleProjectId: entityType === TYPES.RoleProject ? id : undefined,
		},
	});
};

const getUserRoleId = (userId: User['id']) => {
	return resultResourcePlanning.value?.Project?.members.find((member) => {
		return member.id === userId;
	})?.role?.id;
};

const handleResourceUtilizationResponse = ({
	UserUtilization,
}: ResourcePlanningUtilizationQuery) => {
	UserUtilization?.forEach((row) => {
		if (row.userId) {
			config.setUtilizationData(TYPES.User, row.userId, row.utilizations);

			delete config.cachedHeatMapData[getChartResourceId(row.userId)];
		} else if (row.roleId) {
			config.setUtilizationData(TYPES.Role, row.roleId, row.utilizations);

			projectPlaceholderRoles.value?.forEach((data) => {
				if (data.role.id === row.roleId) {
					delete config.cachedHeatMapData[
						getChartResourceId(data.id, TYPES.RoleProject)
					];
				}
			});
		}
	});

	gantt.render();
};

const loadUtilizationData = (
	userIds: Array<User['id']>,
	roleIds: Array<Role['id']>,
	startDate: Date,
	endDate: Date
) => {
	const startDateString = dateToMysqlString(startDate);
	const endDateString = dateToMysqlString(endDate);

	client
		.query({
			query: ResourcePlanningUtilizationDocument,
			variables: {
				input: {
					userIds,
					roleIds,
					startDate: startDateString,
					endDate: endDateString,
				},
			},
			fetchPolicy: 'no-cache',
		})
		.then((response) => {
			handleResourceUtilizationResponse(response.data);
		});

	if (userIds.length > 0) {
		loadTimeOffData(userIds, startDate, endDate);
	}
};

const batchLoadUtilizationData = (
	userIds: Array<User['id']>,
	roleIds: Array<Role['id']>
) => {
	const { min_date: startDate, max_date: endDate } = gantt.getState();
	let fetchStartDate = resetTime(startDate);

	while (isBefore(fetchStartDate, endDate)) {
		let fetchEndDate = addMonths(
			fetchStartDate,
			UTILIZATION_FETCH_INTERVAL
		);

		if (isAfter(fetchEndDate, endDate)) {
			fetchEndDate = endDate;
		}

		loadUtilizationData(userIds, roleIds, fetchStartDate, fetchEndDate);

		fetchStartDate = addMonths(fetchStartDate, UTILIZATION_FETCH_INTERVAL);
	}
};

const getRoleIdsFromRoleProjectIds = (roleProjectIds: RoleProject['id'][]) => {
	const roleProjects =
		resultResourcePlanning.value?.Project?.roleProjects?.filter(
			(roleProject) => roleProjectIds?.includes(roleProject.id)
		);
	return roleProjects?.map((roleProjects) => roleProjects.role.id) ?? [];
};

const fetchSingleUtilizationData = (
	id: User['id'] | RoleProject['id'],
	type: typeof TYPES.User | typeof TYPES.RoleProject = TYPES.User
) => {
	batchLoadUtilizationData(
		type === TYPES.User ? [id] : [],
		type === TYPES.RoleProject ? getRoleIdsFromRoleProjectIds([id]) : []
	);
};

const addProjectUser = async (userId: User['id']) => {
	const res = await addProjectMembersMutation({
		input: {
			projectId: String(props.projectId),
			userIds: [userId],
		},
	});

	if (!res?.errors) {
		fetchSingleUtilizationData(userId);
	}
};

const onRemoveProjectUser = (userId: User['id']) => {
	removeProjectMembersMutation({
		input: {
			projectId: String(props.projectId),
			userIds: [userId],
		},
	});
};

const addProjectRole = (roleId: Role['id']) => {
	addProjectMembersMutation({
		input: {
			projectId: String(props.projectId),
			roleIds: [roleId],
		},
	});
};

const onRemoveProjectRole = (roleProjectId: RoleProject['id']) => {
	removeProjectMembersMutation({
		input: {
			projectId: String(props.projectId),
			roleProjectIds: [roleProjectId],
		},
	});
};

const onUpdateBooking = async (
	input: BookingSaveInput,
	oldEntityId: User['id'] | RoleProject['id'],
	oldEntityType: typeof TYPES.User | typeof TYPES.RoleProject
) => {
	const userIds = new Map();
	const roleIds = new Map();

	const newUserId = input.userId || '';
	const isNewUserNotAMember = !projectMemberIds.value.includes(newUserId);

	if (isNewUserNotAMember) {
		await addProjectUser(newUserId);
	}

	const response = await saveBookingMutation(input);

	if (response?.errors) {
		if (isNewUserNotAMember) {
			onRemoveProjectUser(newUserId);
		}

		return;
	}

	closePopOver();

	if (oldEntityType === TYPES.User) {
		userIds.set(oldEntityId, oldEntityId);
		const userRoleId = getUserRoleId(oldEntityId);
		if (userRoleId) {
			roleIds.set(userRoleId, userRoleId);
		}
		batchLoadUtilizationData([...userIds.values()], [...roleIds.values()]);
	}

	if (oldEntityType === TYPES.RoleProject) {
		fetchSingleUtilizationData(oldEntityId, oldEntityType);
	}

	addSuccessMessage({
		message: t('resource-booking-modal__move-success'),
	});
};

const onMoveBookings = async (
	toUserId: User['id'],
	fromUserId?: User['id'],
	fromRoleProjectId?: RoleProject['id']
) => {
	const userIds = new Map();
	const roleIds = new Map();

	const isNewUserNotAMember = !projectMemberIds.value.includes(toUserId);

	if (isNewUserNotAMember) {
		await addProjectUser(toUserId);
	}

	const response = await moveBookings({
		input: {
			projectId: String(props.projectId),
			toUserId,
			fromUserId,
			fromRoleProjectId,
		},
	});

	if (fromUserId) {
		if (response?.errors && isNewUserNotAMember) {
			onRemoveProjectUser(toUserId);

			return;
		}
		userIds.set(fromUserId, fromUserId);
		const fromUserRoleId = getUserRoleId(fromUserId);
		if (fromUserRoleId) {
			roleIds.set(fromUserRoleId, fromUserRoleId);
		}
		batchLoadUtilizationData([...userIds.values()], [...roleIds.values()]);
	}

	if (fromRoleProjectId) {
		fetchSingleUtilizationData(fromRoleProjectId, TYPES.RoleProject);
	}
};

const onAssignBookings = async (
	toUserId: User['id'],
	fromRoleProjectId: RoleProject['id']
) => {
	const isNewUserNotAMember = !projectMemberIds.value.includes(toUserId);

	if (isNewUserNotAMember) {
		await addProjectUser(toUserId);
	}

	const response = await moveBookings({
		input: {
			projectId: String(props.projectId),
			toUserId,
			fromRoleProjectId,
		},
	});

	if (response) {
		onRemoveProjectRole(fromRoleProjectId);
	}
};

addEventListener('click', '.js-add-project-member', (e) => {
	const popOverTarget = (e.target as Element)?.closest(
		'.js-add-project-member'
	) as HTMLElement | null;

	if (!popOverTarget) {
		return;
	}

	const placement = popOverTarget.dataset.placement as Placement | undefined;

	openProjectMemberDropdown(
		popOverTarget,
		{
			onUserSelected: (id: User['id']) => addProjectUser(id),
			onRoleSelected: (id: Role['id']) => addProjectRole(id),
			enableRolesSelect: isRolesEnabled.value,
		},
		{
			placement,
		}
	);
});

const updateBookingAfterTaskUpdate = (item: Required<ResourcePlanningItem>) => {
	const entityId = getResourceId(item[RESOURCE_PROPERTY]);
	const entityType = getResourceType(item[RESOURCE_PROPERTY]);

	const input: BookingSaveInput = {
		id: item.id,
		projectId: String(config.getProjectId()),
		userId: entityType === TYPES.User ? entityId : null,
		roleProjectId: entityType === TYPES.RoleProject ? entityId : null,
		amountType: item.booking_type,
		amount: item.booked_duration,
		startDate: dateToMysqlString(item.start_date),
		endDate: dateToMysqlString(addDays(item.end_date, -1)),
		isTentative: item.is_tentative,
		description: item.description,
	};

	saveBookingMutation(input);
};

const setFetchedUtilizationDataInterval = () => {
	const { min_date: minDate, max_date: maxDate } = gantt.getState();

	fetchedUtilizationDataInterval.startDate = minDate;
	fetchedUtilizationDataInterval.endDate = maxDate;
};

const removeTimeOffForUsers = (userIds: string[]) => {
	config.deleteTimeOffData(userIds);
};

const reFetchUtilizationDataIfNeeded = () => {
	const { min_date: minDate, max_date: maxDate } = gantt.getState();
	const { startDate, endDate } = fetchedUtilizationDataInterval;

	if (
		(startDate && isBefore(minDate, startDate)) ||
		(endDate && isAfter(maxDate, endDate))
	) {
		batchLoadUtilizationData(
			projectMemberIds.value,
			getRoleIdsFromRoleProjectIds(projectRoleIds.value)
		);

		setFetchedUtilizationDataInterval();
	}
};

const bindGanttEventsToElements = () => {
	gantt.attachEvent(
		'onGanttRender',
		() => {
			if (isInitialRender.value) {
				isInitialRender.value = false;

				if (!config.isReadOnly) {
					addPlaceholderUnderProject(config);
				}

				fixTooltipPositioning(gantt);
				config.attachWeekDatesTooltip();
			}

			reFetchUtilizationDataIfNeeded();
			cleanUpVueInstances();
		},
		{}
	);

	gantt.ext.zoom.attachEvent('onAfterZoom', (zoom: number) => {
		reFetchUtilizationDataIfNeeded();

		zoomLevel.value = zoom;
	});

	gantt.ext.inlineEditors.attachEvent(
		'onBeforeEditStart',
		(state: NotWorthIt) => {
			const item = gantt.getTask(state.id);

			if (!item || item.item_type === CHART_ITEM_TYPE.PLACEHOLDER) {
				return false;
			}

			return true;
		}
	);

	gantt.attachEvent(
		'onTaskClick',
		(id, e) => {
			const item = gantt.getTask(id);

			if (!item) {
				return false;
			}

			const clickOnActionsShortcut = e.target.closest(
				'.resource-planning-actions-shortcut'
			);
			const clickOnCloseIcon = e.target.closest('.gantt_close');

			if (
				item.item_type === CHART_ITEM_TYPE.PROJECT &&
				clickOnCloseIcon
			) {
				gantt.batchUpdate(() => {
					gantt.getChildren(item.id).forEach((itemId) => {
						if (resourcePlanningStore.isMembersListCollapsed) {
							gantt.open(itemId);
						} else {
							gantt.close(itemId);
						}
					});
				});

				resourcePlanningStore.toggleIsProjectCollapsed();

				return false;
			}

			const clickOnOpenIcon = e.target.closest('.gantt_open');
			const clickOnIconToggle = clickOnCloseIcon || clickOnOpenIcon;

			if (
				(item.item_type === CHART_ITEM_TYPE.RESOURCE ||
					item.item_type === CHART_ITEM_TYPE.ROLE) &&
				clickOnIconToggle &&
				!clickOnActionsShortcut
			) {
				if (item.$open) {
					gantt.close(item.id);
				} else {
					gantt.open(item.id);
				}
			}

			if (config.isReadOnly) {
				return false;
			}

			if (
				item.item_type === CHART_ITEM_TYPE.BOOKING &&
				!clickOnActionsShortcut
			) {
				config.onBookingClick(id, e);
			}

			const clickOnPlaceholderIcon = e.target.closest(
				'.resource-planning-placeholder-icon'
			);

			if (
				item.item_type === CHART_ITEM_TYPE.ROLE &&
				clickOnPlaceholderIcon
			) {
				const entityId = getResourceId(item.id);

				openProjectMemberDropdown(
					e.target,
					{
						title: t('resource-booking__move-bookings'),
						projectId: String(config.getProjectId()),
						selectedEntityId: entityId,
						selectedEntityType: TYPES.RoleProject,
						excludeUserId: null,
						onUserSelected: (userId, name) => {
							openConfirmationModal({
								message:
									'resource_booking_assign_member_confirm',
								messageValue: {
									roleName: item.text,
									userName: name,
								},
								isInitiallyOpen: true,
								onConfirm: async () => {
									await onAssignBookings(userId, entityId);
								},
							});
						},
					},
					{
						placement: 'bottom-end',
					}
				);
			}

			return true;
		},
		{}
	);

	gantt.attachEvent(
		'onBeforeTaskDrag',
		() => {
			config.dragInProgress = true;

			return true;
		},
		{}
	);

	gantt.attachEvent(
		'onAfterTaskDrag',
		() => {
			config.dragInProgress = false;
			gantt.render();
		},
		{}
	);

	gantt.attachEvent(
		'onAfterTaskUpdate',
		(id, item: Required<ResourcePlanningItem>) => {
			if (item.item_type === CHART_ITEM_TYPE.BOOKING) {
				updateBookingAfterTaskUpdate(item);
			}
		},
		{}
	);

	gantt.attachEvent(
		'onBeforeGanttRender',
		() => {
			config.setChartMinMaxDate();
		},
		{}
	);
};

const onChartClick = (payload: ChartInteractionPayload) => {
	const { resource, date, target } = payload;

	if (!resource || !date) {
		target.remove();

		return;
	}

	const { startDate, endDate } = getBookingStartAndEndDateByZoomLevel(date);
	const zoomLevelName = ZOOM_SCALE_OPTIONS[config.getZoomLevelSetting()];

	let entityId = getResourceId(resource.id);
	const entityType = getResourceType(resource.id);
	let newEntityType: typeof TYPES.User | typeof TYPES.Role = TYPES.User;

	if (entityType === TYPES.RoleProject) {
		entityId =
			resultResourcePlanning.value?.Project?.roleProjects?.find(
				(roleProject) => roleProject.id === entityId
			)?.role.id || '';
		newEntityType = TYPES.Role;
	}

	if (resource.row_type === CHART_ROW_TYPE.HEATMAP) {
		openProjectsPopover(target, {
			startDate,
			endDate,
			userIds: newEntityType === TYPES.User ? [entityId] : [],
			roleIds: newEntityType === TYPES.Role ? [entityId] : [],
			filterValue: bookingTypeFilter.value,
			hasTentativeBookingsFeature: hasTentativeBookingsFeature.value,
		});
	} else if (resource.row_type === CHART_ROW_TYPE.BOOKINGS) {
		const entityId = getResourceId(resource.id);
		let entityName: string | undefined = undefined;

		if (entityType === TYPES.RoleProject) {
			entityName =
				resultResourcePlanning.value?.Project?.roleProjects?.find(
					(roleProject) => roleProject.id === entityId
				)?.role.name || '';
		}

		const props: ResourcePlanningBookingModalProps = {
			startDate,
			endDate,
			zoomLevel: zoomLevelName,
			projectStartDate: resetTime(config.projectStartDate),
			projectId: String(config.getProjectId()),
			selectedEntityId: entityId,
			selectedEntityType:
				entityType === TYPES.User ? TYPES.User : TYPES.RoleProject,
			selectedEntityName: entityName,
			amount: DEFAULT_BOOKING_AMOUNT.PERCENT,
			amountType: BookingAmountType.Percent,
			isTentative: entityType === TYPES.RoleProject,
			hasTentativeBookingsFeature: hasTentativeBookingsFeature.value,
		};

		openBookingModal(null, props, {
			placement: 'top',
		});
	}
};

const initChart = (result: ResourcePlanningQuery) => {
	if (!isInitialLoad.value) {
		// CLean up old gantt instance, events
		gantt.destructor();
		gantt = window.Gantt.getGanttInstance() as Gantt;
		config.setGantt(gantt);
		isInitialRender.value = true;
	}

	const { Project: project, BookingsList: bookings } = result;
	const projectId = `${CHART_ITEM_TYPE.PROJECT}-${props.projectId}`;
	const { startDate: plannedStartDate, endDate: plannedEndDate } =
		getProjectPlannedDates(project);

	resourcePlanningStore.addSelectedUserIds(
		project?.members?.map((user) => user.id) || []
	);

	projectMembers.value = project?.members || [];
	projectPlaceholderRoles.value = project?.roleProjects || [];

	config.setIsReadOnly(!project?.permissions?.canModify);
	config.setIsSingleProject(true);
	config.setProjectColor(projectColor.value);
	config.setContainerElem(
		document.getElementById(RESOURCE_PLANNING_CONTAINER_ID) as HTMLElement
	);
	config.setChartProjectId(projectId);
	config.setOnProjectUserRemoved(onRemoveProjectUser);
	config.setOnProjectRoleRemoved(onRemoveProjectRole);
	config.setOnMoveBookings(onMoveBookings);
	config.setOnAssignBookings(onAssignBookings);
	config.setOnUpdateBooking(onUpdateBooking);
	config.setOnClearBookings(onClearBookings);
	config.setProjectStartDate(project?.date ?? '');
	config.setProjectManagerId(project?.manager?.id ?? '');
	config.setProjectPlannedStartDate(plannedStartDate);
	config.setProjectPlannedEndDate(plannedEndDate);
	config.setProjectMemberIds(projectMemberIds.value);
	config.setProjectRetainers(project?.retainer?.periods || []);
	config.setHeatMapDisplayValue(heatMapValue.value);
	config.setBookingTypeFilterValue(bookingTypeFilter.value);

	config.init(RESOURCE_PLANNING_CONTAINER_ID);
	config.addTodaysMarker();

	const data: ResourcePlanningItem[] = [];

	const resources: ResourcePlanningItem[] = [
		{
			group_id: projectId,
			id: projectId,
			text: project?.name ?? '',
			color: project?.color,
			item_type: CHART_ITEM_TYPE.PROJECT,
			row_type: CHART_ROW_TYPE.OTHER,
			open: true,
			start_date: config.getProjectPlannedStartDate(),
			end_date:
				config.getProjectPlannedEndDate() ??
				config.getProjectPlannedStartDate(),
			[RESOURCE_PROPERTY]: getUniqueId(),
			parent: null,
			unscheduled: true,
			project_icon_name: getProjectIconName(project as Project),
		},
	];

	// Project owner always first in the list
	const projectManager = project?.members.find(
		(user) => user.id === project?.manager?.id
	);

	if (projectManager) {
		resources.push(...getResourceAndBookingsRow(projectManager, config));
	}

	// Project members sorted alphabetically
	[
		...(project?.members.filter(
			(user) => user.id !== project?.manager?.id
		) || []),
	]
		.sort(sortAlphabetically('name'))
		.forEach((user) => {
			resources.push(...getResourceAndBookingsRow(user, config));
		});

	// Roles sorted alphabetically
	[...(project?.roleProjects || [])]
		?.sort(sortAlphabeticallyBySubKey('role', 'name'))
		.forEach((roleProjects) => {
			resources.push(...getResourceAndBookingsRow(roleProjects, config));
		});

	bookings.forEach((booking) => {
		const projectId = result.Project?.id;

		// TODO: move the logic to `onBeforeSplitTaskDisplay` after library update to 8.x.x
		const isVisible = isBookingVisible(
			bookingTypeFilter.value,
			booking.isTentative,
			projectId
		);

		if (isVisible) {
			const item = config.bookingToChartItem(booking);
			const relatedResource = resources.find(
				(res) => res.id === item[RESOURCE_PROPERTY]
			);

			if (relatedResource) {
				data.push(item);
			} else {
				bookingsWithoutResource.value[booking.id] = booking;
			}
		}
	});

	gantt.parse({
		data,
	});

	config.groupBy(resources);
	bindGanttEventsToElements();
	config.addResourcePlanningToolLayers();
	zoomToLevel(config.getZoomLevelSetting());
	batchLoadUtilizationData(projectMemberIds.value, [
		...new Set(projectRoleIds.value),
	]);
	setFetchedUtilizationDataInterval();

	const item = gantt.getTask(projectId);

	if (item && resourcePlanningStore.isMembersListCollapsed) {
		gantt.batchUpdate(() => {
			gantt.getChildren(item.id).forEach((itemId) => {
				gantt.close(itemId);
			});
		});
	}

	if (isInitialLoad.value) {
		window.addEventListener(MISSING_AVAILABILITY_LOADED, () => {
			config.refreshData();
		});
		window.addEventListener('resize', () => {
			config.clearCachedHeatMapElements();
		});
	}
};

const updateResourceData = async () => {
	await refetchResourcePlanning();

	const isResourcePlanningTab = window.location.pathname.startsWith(
		`/${Constants.Module.PROJECTS.name}/${Constants.Action.VIEW}/${props.projectId}${PROJECT_TABS.ResourcePlanning.link}`
	);

	if (resultResourcePlanning.value && isResourcePlanningTab) {
		initChart(resultResourcePlanning.value);
	}
};

onMounted(() => {
	// The framework can be initialized only after the component has been rendered
	onResultResourcePlanning((result) => {
		if (!isInitialLoad.value) {
			return;
		}

		const data = result.data;

		projectColor.value = data.Project?.color ?? '';
		initChart(data);
		isInitialLoad.value = false;
	});

	config.setRetainerPeriodId(props.retainerPeriodId?.toString() || '');
	addTimeOffHighlightClickEventHandler();
});

const updateUserRoleAvailability = (userIds: Array<User['id']>) => {
	const roleIdsUpdated = new Set<Role['id']>();
	userIds.forEach((userId) => {
		const user = resultResourcePlanning.value?.Project?.members.find(
			(user) => user.id === userId
		);

		if (
			user?.role?.id &&
			resultResourcePlanning.value?.Project?.roleProjects?.find(
				(roleProject) => roleProject.role.id === user.role?.id
			)
		) {
			roleIdsUpdated.add(user.role.id);
		}
	});

	const roleIdsToRefetch = Array.from(roleIdsUpdated);
	if (roleIdsToRefetch.length > 0) {
		batchLoadUtilizationData([], roleIdsToRefetch);
	}
};

const handleProjectSocketMessage = (
	message: BaseSocketMessage<
		'projectTimeframeChanged' | 'projectChanged',
		{
			project_id: number;
		}
	>
) => {
	if (props.projectId === String(message.data.project_id)) {
		updateResourceData();
	}
};

useSocket({
	[SocketMessageType.BOOKING_CREATED]: (message) => {
		const booking = message.data;

		if (String(booking.project.id) !== config.getProjectId()) {
			return;
		}

		let resourceId = '';
		if (message.data.user) {
			resourceId = getChartResourceId(message.data.user.id);
		} else if (message.data.roleProject) {
			resourceId = getChartResourceId(
				message.data.roleProject.id,
				TYPES.RoleProject
			);
		}

		const resource = gantt.getTask(resourceId);

		if (!resource) {
			bookingsWithoutResource.value[booking.id] = booking;
			return;
		}

		const isVisible = isBookingVisible(
			bookingTypeFilter.value,
			booking.isTentative,
			resultResourcePlanning.value?.Project?.id
		);

		if (isVisible) {
			config.addItemToChart(config.bookingToChartItem(booking));
		}

		if (message.data.user?.id) {
			fetchSingleUtilizationData(message.data.user?.id.toString());
			updateUserRoleAvailability([message.data.user?.id.toString()]);
		} else if (message.data.roleProject?.id) {
			fetchSingleUtilizationData(
				message.data.roleProject.id.toString(),
				TYPES.RoleProject
			);
		}
	},
	[SocketMessageType.BOOKING_UPDATED]: (message) => {
		const booking = message.data;

		if (String(booking.project.id) !== config.getProjectId()) {
			return;
		}

		const item = gantt.getTask(booking.id);
		const entityId = message.data.user?.id || message.data.roleProject?.id;
		const entityType = message.data.roleProject?.id
			? TYPES.RoleProject
			: TYPES.User;

		const resource = gantt.getTask(
			getChartResourceId(entityId || '', entityType)
		);

		if (!item || !resource) {
			bookingsWithoutResource.value[booking.id] = booking;
			return;
		}

		Object.assign(item, config.bookingToChartItem(booking));

		const isVisible = isBookingVisible(
			bookingTypeFilter.value,
			booking.isTentative,
			resultResourcePlanning.value?.Project?.id
		);

		if (isVisible) {
			config.updateChartItem(item.id);
		} else {
			config.deleteItemFromChart(item.id);
		}

		// we rerender gantt after fetching the heatmap data for user
		if (message.data.user || message.data.roleProject) {
			if (message.data.user) {
				const userIds = new Map();
				const roleIds = new Map();
				const userId = message.data.user.id.toString();
				userIds.set(userId, userId);

				const userRoleId = getUserRoleId(userId);
				if (userRoleId) {
					roleIds.set(userRoleId, userRoleId);
				}
				batchLoadUtilizationData(
					[...userIds.values()],
					[...roleIds.values()]
				);
			} else if (message.data.roleProject) {
				fetchSingleUtilizationData(
					message.data.roleProject.id.toString(),
					TYPES.RoleProject
				);
			}
		} else {
			gantt.render();
		}
	},
	[SocketMessageType.BOOKINGS_DELETED]: (message) => {
		const { ids: bookingIds } = message.data;

		const userIdsToRefetch: User['id'][] = [];
		const roleProjectIdsToRefetch: RoleProject['id'][] = [];

		bookingIds.forEach((id) => {
			const task = gantt.getTask(id);
			if (task) {
				config.deleteItemFromChart(id);
				const entityId = getResourceId(task.resource_id).toString();
				const entityType = getResourceType(task.resource_id);

				if (entityType === TYPES.User) {
					if (!userIdsToRefetch.includes(entityId)) {
						userIdsToRefetch.push(entityId);
					}
				} else if (entityType === TYPES.RoleProject) {
					if (!roleProjectIdsToRefetch.includes(entityId)) {
						roleProjectIdsToRefetch.push(entityId);
					}
				}
			}
		});

		batchLoadUtilizationData(
			userIdsToRefetch,
			getRoleIdsFromRoleProjectIds(roleProjectIdsToRefetch)
		);

		updateUserRoleAvailability(userIdsToRefetch);
	},
	[SocketMessageType.PROJECT_MEMBERS_CHANGED]: async (message) => {
		if (props.projectId !== String(message.data.projectId)) {
			return;
		}

		const {
			removedUserIds,
			addedUserIds,
			removedRoleProjects,
			addedRoleProjects,
		} = message.data;

		if (addedUserIds.length > 0 || addedRoleProjects.length > 0) {
			await updateResourceData();

			const bookingsToProcess = Object.values(
				bookingsWithoutResource.value
			);
			bookingsToProcess.forEach((booking) => {
				const entityId = booking.user?.id || booking.roleProject?.id;
				const entityType = booking.roleProject?.id
					? TYPES.RoleProject
					: TYPES.User;
				const resourceId = getChartResourceId(
					entityId ?? '',
					entityType
				);
				const resource = gantt.getTask(resourceId);

				if (resource) {
					const isVisible = isBookingVisible(
						bookingTypeFilter.value,
						booking.isTentative,
						resultResourcePlanning.value?.Project?.id
					);

					if (isVisible) {
						config.addItemToChart(
							config.bookingToChartItem(booking)
						);
						delete bookingsWithoutResource.value[booking.id];
					}
				}
			});

			return;
		}

		removedUserIds.forEach((userId) => {
			config.deleteItemFromChart(getChartResourceId(userId));
			resourcePlanningStore.removeSelectedUserId(userId.toString());
		});

		removedRoleProjects.forEach((roleProject) => {
			config.deleteItemFromChart(
				getChartResourceId(roleProject.id, TYPES.RoleProject)
			);
		});
	},
	[SocketMessageType.PROJECT_TIMEFRAME_CHANGED]: handleProjectSocketMessage,
	[SocketMessageType.PROJECT_CHANGED]: handleProjectSocketMessage,
	[SocketMessageType.TIME_OFF_UPDATED]: async (message) => {
		const { userIds } = message.data;

		if (userIds.length > 0) {
			removeTimeOffForUsers(userIds);
			config.clearCachedSubRowHeatMapData(userIds);
			batchLoadUtilizationData(userIds, []);
		}
	},
	[SocketMessageType.TIME_OFF_DELETED]: async (message) => {
		const { userIds } = message.data;

		if (userIds.length > 0) {
			removeTimeOffForUsers(userIds);
			config.clearCachedSubRowHeatMapData(userIds);
			batchLoadUtilizationData(userIds, []);
		}
	},
});
</script>

<style lang="scss">
@import '@/../../inc/_components/ganttChart/dhtmlxgantt.css';
@import '@/../../inc/_components/ganttChart/dhtmlxgantt.custom';
@import '@/../../inc/_components/ganttChart/ganttChart';
@import './resourcePlanning';

.resource-planning {
	&__loader {
		display: flex;
		padding: 0 5%;
		flex-wrap: wrap;
		gap: 1rem;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: var(--project-page-max-width);
		padding: 1rem var(--project-page-side-spacing);
		margin: auto;
	}

	&__actions-shortcut {
		display: flex;
		align-items: center;
		height: 100%;

		> .v-popper {
			height: 100%;
		}
	}
}
</style>
